import { AlertTriangle, AlertCircle } from "lucide-react";
import type { FeatureDependency } from "@/types";

interface CircularWarning {
	type: "circular";
	message: string;
	chain?: string[];
}

interface ImpactWarning {
	type: "impact";
	feature: string;
	dependentCount: number;
	dependents: string[];
}

type Warning = CircularWarning | ImpactWarning;

interface ValidationWarningsProps {
	warnings: Warning[];
}

export function ValidationWarnings({ warnings }: ValidationWarningsProps) {
	if (warnings.length === 0) return null;

	return (
		<div className="space-y-2">
			{warnings.map((warning, index) => {
				if (warning.type === "circular") {
					return (
						<div
							key={index}
							className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200"
						>
							<AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
							<div className="flex-1">
								<p className="text-sm font-medium text-red-800">
									Circular Dependency Detected
								</p>
								<p className="text-sm text-red-700">{warning.message}</p>
								{warning.chain && warning.chain.length > 0 && (
									<p className="text-xs text-red-600 mt-1 font-mono">
										{warning.chain.join(" → ")}
									</p>
								)}
							</div>
						</div>
					);
				}

				if (warning.type === "impact") {
					return (
						<div
							key={index}
							className="flex items-start gap-2 p-3 rounded-md bg-yellow-50 border border-yellow-200"
						>
							<AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
							<div className="flex-1">
								<p className="text-sm font-medium text-yellow-800">
									Impact Warning
								</p>
								<p className="text-sm text-yellow-700">
									Removing <span className="font-mono">{warning.feature}</span>{" "}
									will affect {warning.dependentCount} other feature(s)
								</p>
								{warning.dependents.length > 0 && (
									<p className="text-xs text-yellow-600 mt-1">
										Dependents: {warning.dependents.slice(0, 5).join(", ")}
										{warning.dependents.length > 5 &&
											` and ${warning.dependents.length - 5} more`}
									</p>
								)}
							</div>
						</div>
					);
				}

				return null;
			})}
		</div>
	);
}

interface CircularDependencyCheckProps {
	featureCode: string;
	targetFeature: string;
	dependencyMap: Record<string, FeatureDependency[]>;
}

/**
 * Check if adding a dependency would create a circular reference
 * @returns The circular chain if detected, null otherwise
 */
export function detectCircularDependency({
	featureCode,
	targetFeature,
	dependencyMap,
}: CircularDependencyCheckProps): string[] | null {
	const visited = new Set<string>();
	const path: string[] = [];

	function dfs(current: string): boolean {
		if (current === featureCode) {
			path.push(current);
			return true; // Found circular dependency back to original feature
		}

		if (visited.has(current)) {
			return false;
		}

		visited.add(current);
		path.push(current);

		const dependencies = dependencyMap[current] || [];
		for (const dep of dependencies) {
			if (dfs(dep.feature)) {
				return true;
			}
		}

		path.pop();
		return false;
	}

	// Start DFS from the target feature
	if (dfs(targetFeature)) {
		return [featureCode, ...path];
	}

	return null;
}
